/**
 * The charts of the model metrics page (TRA-1461), all over the same
 * buckets so a bucket sits at the same x on every one: one requests line
 * per status class, four percentile lines (latency and time to first token),
 * tokens per second, tokens per bucket, requests in flight, prompt tokens
 * per second, tokens per request, the prompt cache rate, and the error
 * rate.
 *
 * Colour is matched across the page (index.css, the --chart-* tokens):
 * p50 to p99 wear chart-1 to chart-4 on both percentile charts (chart-4 is
 * the muted red, so p99 stands out as the line to watch), input and
 * output tokens wear chart-1 and chart-2 wherever the pair appears, a
 * single-series chart wears chart-1, and a status class wears its chart
 * status token (chart-2xx, chart-4xx, chart-5xx, chart-other), and so does
 * the error rate line. Every chart carries a headline number for the
 * window and a legend whose entries show the window value, so a reader
 * never has to match a hue to a line. The hover readout lists the bucket's
 * numbers; the wrapper's aria-label says what the lines cannot.
 *
 * The value axis sizes itself to its labels (Recharts' `width="auto"`) and
 * the labels are short (contracts.ts, the tick formatters), so no tick is
 * ever clipped: "300,000,000" used to show as "00000" on tokens per bucket.
 *
 * Every chart goes through BucketLineChart or BucketBarChart, so a drag
 * across any of them zooms the page (TRA-1592, useDragZoom) the same way.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { formatTime } from '@/components/models/home/scalingWindow'
import { ReadoutRow } from '@/components/models/home/RequestsPanel'
import { PERCENTILE_HINT, formatCompact, formatRate } from '@/components/models/home/contracts'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { ChartControls, type PagePan } from '@/components/models/home/PanControls'
import { dragShade, useDragZoom } from '@/components/models/home/useDragZoom'
import type {
  MetricsBucket,
  MetricsSummary,
  Percentiles,
} from '@/components/models/metrics/contracts'
import {
  STATUS_LEGEND,
  drawnStatusLines,
  errorAxisMax,
  extent,
  formatCountTick,
  formatMs,
  formatMsTick,
  formatPercent,
  formatPercentTick,
  formatTokens,
} from '@/components/models/metrics/contracts'
import { bucketDragRange } from '@/components/models/metrics/metricsZoom'

const AXIS_TICK = { fontSize: 10, fill: 'hsl(var(--muted-foreground))' }
const GRID_STROKE = 'hsl(var(--border) / 0.6)'
const CURSOR = { stroke: 'hsl(var(--muted-foreground) / 0.4)', strokeDasharray: '3 3' }
const BAR_CURSOR = { fill: 'hsl(var(--muted) / 0.6)' }
const CHART_MARGIN = { top: 6, right: 0, bottom: 0, left: 0 }
const DASH = '—'

export const CHART_1 = 'hsl(var(--chart-1))'
export const CHART_2 = 'hsl(var(--chart-2))'
const CHART_3 = 'hsl(var(--chart-3))'
const CHART_4 = 'hsl(var(--chart-4))'
/** The error line means failure, so it wears the 5xx colour, not a series colour. */
const ERROR_STROKE = 'hsl(var(--chart-5xx))'

/** What the in-flight number is, shown on hover: the same caveat as the replicas chart's note. */
const IN_FLIGHT_HINT =
  'Each bucket multiplies its request count by its mean latency and divides by its length. The result is an average over the bucket, so a short spike is hidden.'

interface LineSeries {
  key: keyof Percentiles
  label: string
  stroke: string
}

/** Drawn in this order, so p50 lands on top. */
const PERCENTILE_LINES: LineSeries[] = [
  { key: 'p99', label: 'p99', stroke: CHART_4 },
  { key: 'p95', label: 'p95', stroke: CHART_3 },
  { key: 'p90', label: 'p90', stroke: CHART_2 },
  { key: 'p50', label: 'p50', stroke: CHART_1 },
]

/** Legend order: p50 first, as the reader thinks of them. */
const PERCENTILE_LEGEND = [...PERCENTILE_LINES].reverse()

/** The input/output pair, the same two colours on every chart that shows it. */
const INPUT = { label: 'Input', color: CHART_1 }
const OUTPUT = { label: 'Output', color: CHART_2 }

/* --------------------------------------------------------------- legend */

export function LineMark({ stroke }: { stroke: string }): React.JSX.Element {
  return (
    <svg width="16" height="2" aria-hidden>
      <line x1="0" y1="1" x2="16" y2="1" stroke={stroke} strokeWidth={2} />
    </svg>
  )
}

function Swatch({ fill }: { fill: string }): React.JSX.Element {
  return <span aria-hidden className="inline-block h-2 w-2" style={{ background: fill }} />
}

export interface LegendItem {
  label: string
  mark: React.ReactNode
  /** The window's value for this series. */
  value: string
}

export function Legend({ items }: { items: LegendItem[] }): React.JSX.Element {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-2 font-mono text-[10px] tracking-wider">
          {item.mark}
          <span className="uppercase text-muted-foreground">{item.label}</span>
          <span className="tabular-nums text-foreground">{item.value}</span>
        </li>
      ))}
    </ul>
  )
}

/* ------------------------------------------------------------- headline */

/** The window's one number for a card, above its chart. */
export function Headline({
  value,
  label,
  hint,
}: {
  value: string
  label: string
  /** What the number is not, shown on hover. */
  hint?: string
}): React.JSX.Element {
  return (
    <div title={hint}>
      <p className="font-mono text-2xl leading-none tabular-nums">{value}</p>
      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
    </div>
  )
}

/* -------------------------------------------------------------- readout */

interface ReadoutLine {
  label: string
  value: string
}

/**
 * The hover box. Recharts hands the hovered row as `payload[0].payload`;
 * each chart says which of its numbers to list. The row is a metrics
 * bucket unless a chart says otherwise (the engine cards' buckets).
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is the row type behind Recharts' untyped payload; it reaches `lines` and nothing else can carry it
function Readout<T extends { label: string } = MetricsBucket>({
  active,
  payload,
  unit,
  lines,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
  unit: string
  lines: (bucket: T) => ReadoutLine[]
}): React.JSX.Element | null {
  const bucket = payload?.[0]?.payload as T | undefined
  if (!active || !bucket) return null
  return (
    <div className="w-56 border border-border bg-card p-3">
      <p className="flex items-baseline justify-between gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="text-foreground">{bucket.label}</span>
        <span>{unit}</span>
      </p>
      <dl className="mt-2.5 space-y-1 text-xs">
        {lines(bucket).map((line) => (
          <ReadoutRow key={line.label} label={line.label} value={line.value} />
        ))}
      </dl>
    </div>
  )
}

/* ---------------------------------------------------------------- frame */

/**
 * Headline (or a caption) with the legend beside it, then a fixed-height
 * plot with the page's arrows and Reset zoom on it (TRA-1592). The controls
 * sit next to the image box, not inside it: `role="img"` folds everything
 * under it into one picture, so a button there is lost to a screen reader.
 */
export function Frame({
  lead,
  legend,
  alt,
  pan,
  children,
}: {
  lead: React.ReactNode
  legend: React.ReactNode
  /** What a screen reader gets instead of the drawing. */
  alt: string
  /** The page's arrows and Reset zoom, drawn on every chart. */
  pan: PagePan
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        {lead}
        {legend}
      </div>
      <div className="group relative mt-4 h-44 w-full select-none">
        <ChartControls pan={pan} />
        <div className="h-full w-full" role="img" aria-label={alt}>
          {children}
        </div>
      </div>
    </div>
  )
}

/** The small title of a chart that is not a card's first. */
export function Caption({ children }: { children: string }): React.JSX.Element {
  return (
    <p className="font-mono text-[10px] uppercase tracking-wider text-foreground">{children}</p>
  )
}

function TimeAxis({ hours }: { hours: number }): React.JSX.Element {
  return (
    <XAxis
      dataKey="epoch"
      tickFormatter={(epoch: number) => formatTime(epoch, hours)}
      minTickGap={48}
      tickLine={false}
      axisLine={false}
      tick={AXIS_TICK}
    />
  )
}

/**
 * The y axis: short labels (`format`) on an axis as wide as its longest
 * one. `allowDecimals` is for a rate axis whose values sit below one, such
 * as requests in flight on a quiet model; a whole-number axis there would
 * be 0 and 1 with the line flat on the floor.
 */
export function ValueAxis({
  format,
  max,
  allowDecimals = false,
}: {
  format: (value: number) => string
  max?: number
  allowDecimals?: boolean
}): React.JSX.Element {
  return (
    <YAxis
      domain={[0, max ?? 'auto']}
      tickCount={4}
      interval={0}
      allowDecimals={allowDecimals}
      tickFormatter={format}
      tickLine={false}
      axisLine={false}
      width="auto"
      tickMargin={12}
      tick={AXIS_TICK}
    />
  )
}

function Grid(): React.JSX.Element {
  return <CartesianGrid stroke={GRID_STROKE} vertical={false} />
}

/**
 * Where a drag across a chart goes (TRA-1592): the page's zoom. A drag
 * covers whole buckets, from the first one dragged to the end of the last.
 */
export interface DragZoom {
  bucketMs: number
  onZoom: (zoom: TimeRange) => void
  /** The page's arrows and Reset zoom, which each chart's Frame draws. */
  pan: PagePan
}

function useBucketDrag(drag: DragZoom): ReturnType<typeof useDragZoom> {
  return useDragZoom(drag.onZoom, (from, to) => bucketDragRange(from, to, drag.bucketMs))
}

interface BucketChartProps<T extends { label: string }> {
  data: T[]
  hours: number
  drag: DragZoom
  /** A `ValueAxis`. */
  axis: React.ReactNode
  unit: string
  /** The hovered bucket's numbers, as the readout lists them. */
  lines: (bucket: T) => ReadoutLine[]
  /** The `SeriesLine`s or `Bar`s, back to front. */
  children: React.ReactNode
}

/**
 * A line chart over a window's buckets: the page's grid, time axis, hover
 * readout and drag zoom around the given value axis and lines, so every
 * chart on the page reads and zooms alike. The engine section draws its
 * cards with it too.
 */
export function BucketLineChart<T extends { label: string }>({
  data,
  hours,
  drag,
  axis,
  unit,
  lines,
  children,
}: BucketChartProps<T>): React.JSX.Element {
  const { drag: dragged, handlers } = useBucketDrag(drag)
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      <LineChart data={data} margin={CHART_MARGIN} {...handlers}>
        <Grid />
        <TimeAxis hours={hours} />
        {axis}
        <Tooltip
          cursor={CURSOR}
          content={({ active, payload }) => (
            <Readout<T> active={active} payload={payload} unit={unit} lines={lines} />
          )}
        />
        {children}
        {dragShade(dragged)}
      </LineChart>
    </ResponsiveContainer>
  )
}

/** The bar chart of the page, framed like its line charts. */
function BucketBarChart<T extends { label: string }>({
  data,
  hours,
  drag,
  axis,
  unit,
  lines,
  children,
}: BucketChartProps<T>): React.JSX.Element {
  const { drag: dragged, handlers } = useBucketDrag(drag)
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      <BarChart data={data} margin={CHART_MARGIN} barCategoryGap={1} {...handlers}>
        <Grid />
        <TimeAxis hours={hours} />
        {axis}
        <Tooltip
          cursor={BAR_CURSOR}
          content={({ active, payload }) => (
            <Readout<T> active={active} payload={payload} unit={unit} lines={lines} />
          )}
        />
        {children}
        {dragShade(dragged)}
      </BarChart>
    </ResponsiveContainer>
  )
}

/** One line of a bucket chart, drawn like every other line on the page. */
export function SeriesLine({ dataKey, stroke }: { dataKey: string; stroke: string }): React.JSX.Element {
  return (
    <Line
      dataKey={dataKey}
      stroke={stroke}
      strokeWidth={1.5}
      dot={{ r: 1.5, fill: stroke, stroke: 'none' }}
      isAnimationActive={false}
    />
  )
}

/* ---------------------------------------------------------------- charts */

function measured(buckets: MetricsBucket[], pick: (bucket: MetricsBucket) => number | null): number[] {
  return buckets.flatMap((bucket) => {
    const value = pick(bucket)
    return value === null ? [] : [value]
  })
}

interface ChartProps {
  buckets: MetricsBucket[]
  summary: MetricsSummary
  hours: number
  drag: DragZoom
}

/** Requests per bucket, one line per status class on one axis; the headline is the window's rate. */
export function StatusChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const peak = Math.max(...buckets.map((bucket) => bucket.requests))
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatRate(summary.requestsPerMinute)} label="requests / min" />}
      legend={
        <Legend
          items={STATUS_LEGEND.map((line) => ({
            label: line.label,
            mark: <LineMark stroke={line.stroke} />,
            value: summary[line.key].toLocaleString(),
          }))}
        />
      }
      alt={`Requests per bucket by status class, peaking at ${peak.toLocaleString()} in one bucket.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} />}
        unit="requests"
        lines={(bucket) => [
          ...STATUS_LEGEND.map((line) => ({ label: line.label, value: bucket[line.key].toLocaleString() })),
          { label: 'Total', value: bucket.requests.toLocaleString() },
          { label: 'Per minute', value: formatRate(bucket.requestsPerMinute) },
        ]}
      >
        {drawnStatusLines(summary).map((line) => (
          <SeriesLine key={line.key} dataKey={line.key} stroke={line.stroke} />
        ))}
      </BucketLineChart>
    </Frame>
  )
}

/** Four percentile lines over the buckets; latency or time to first token. */
export function PercentileChart({
  buckets,
  hours,
  drag,
  pick,
  summary,
  title,
}: {
  buckets: MetricsBucket[]
  hours: number
  drag: DragZoom
  pick: (bucket: MetricsBucket) => Percentiles
  /** The window's percentiles: the headline p50 and the legend values. */
  summary: Percentiles
  /** Names the chart in its aria-label. */
  title: string
}): React.JSX.Element {
  const rows = buckets.map((bucket) => ({ ...bucket, ...pick(bucket) }))
  const p50s = measured(buckets, (bucket) => pick(bucket).p50)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatMs(summary.p50)} label="p50" hint={PERCENTILE_HINT} />}
      legend={
        <Legend
          items={PERCENTILE_LEGEND.map((line) => ({
            label: line.label,
            mark: <LineMark stroke={line.stroke} />,
            value: formatMs(summary[line.key]),
          }))}
        />
      }
      alt={`${title}: p50 ranged from ${extent(p50s, formatMs)} across this window's buckets.`}
    >
      <BucketLineChart
        data={rows}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatMsTick} />}
        unit="ms"
        lines={(bucket) =>
          PERCENTILE_LEGEND.map((line) => ({
            label: line.label,
            value: formatMs(pick(bucket)[line.key]),
          }))
        }
      >
        {PERCENTILE_LINES.map((line) => (
          <SeriesLine key={line.key} dataKey={line.key} stroke={line.stroke} />
        ))}
      </BucketLineChart>
    </Frame>
  )
}

/** Output tokens per second after the first one, per bucket. */
export function TokensPerSecondChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const rates = measured(buckets, (bucket) => bucket.tokensPerSecond)
  const average = summary.tokensPerSecond === null ? DASH : formatRate(summary.tokensPerSecond)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={average} label="tokens / sec" />}
      legend={<Legend items={[{ label: 'Avg', mark: <LineMark stroke={CHART_1} />, value: average }]} />}
      alt={`Tokens per second ranged from ${extent(rates, formatRate)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} />}
        unit="tokens / sec"
        lines={(bucket) => [
          { label: 'Avg', value: bucket.tokensPerSecond === null ? DASH : formatRate(bucket.tokensPerSecond) },
        ]}
      >
        <SeriesLine dataKey="tokensPerSecond" stroke={CHART_1} />
      </BucketLineChart>
    </Frame>
  )
}

/** Input and output tokens per bucket. */
export function TokensChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const peak = Math.max(...buckets.map((bucket) => bucket.promptTokens + bucket.completionTokens))
  return (
    <Frame
      pan={drag.pan}
      lead={<Caption>Tokens per bucket</Caption>}
      legend={
        <Legend
          items={[
            { label: INPUT.label, mark: <Swatch fill={INPUT.color} />, value: formatCompact(summary.promptTokens) },
            { label: OUTPUT.label, mark: <Swatch fill={OUTPUT.color} />, value: formatCompact(summary.completionTokens) },
          ]}
        />
      }
      alt={`Tokens per bucket, peaking at ${peak.toLocaleString()} in one bucket.`}
    >
      <BucketBarChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} />}
        unit="tokens"
        lines={(bucket) => [
          { label: INPUT.label, value: bucket.promptTokens.toLocaleString() },
          { label: OUTPUT.label, value: bucket.completionTokens.toLocaleString() },
        ]}
      >
        <Bar dataKey="promptTokens" stackId="tokens" fill={INPUT.color} isAnimationActive={false} />
        <Bar dataKey="completionTokens" stackId="tokens" fill={OUTPUT.color} isAnimationActive={false} />
      </BucketBarChart>
    </Frame>
  )
}

/** A load at whatever scale the traffic is ("0.02" on a quiet model, "14" at full tilt); a dash where it is unknown. */
function formatLoad(load: number | null): string {
  return load === null ? DASH : formatRate(load)
}

/**
 * The api's estimate of requests in flight at once, per bucket (TRA-1437's
 * formula). The headline is the window's mean and the legend adds the
 * busiest bucket, because an average over a whole day hides the hour that
 * needed the replicas.
 */
export function ConcurrencyChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const loads = measured(buckets, (bucket) => bucket.inFlight)
  const peak = loads.length === 0 ? null : Math.max(...loads)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatLoad(summary.inFlight)} label="requests in flight" hint={IN_FLIGHT_HINT} />}
      legend={
        <Legend
          items={[
            { label: 'Avg', mark: <LineMark stroke={CHART_1} />, value: formatLoad(summary.inFlight) },
            // The peak is a point on the same line, not a second series, so it gets no mark.
            { label: 'Peak', mark: null, value: formatLoad(peak) },
          ]}
        />
      }
      alt={`Requests in flight ranged from ${extent(loads, formatRate)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} allowDecimals />}
        unit="requests"
        lines={(bucket) => [
          { label: 'In flight', value: formatLoad(bucket.inFlight) },
          { label: 'Requests', value: bucket.requests.toLocaleString() },
        ]}
      >
        <SeriesLine dataKey="inFlight" stroke={CHART_1} />
      </BucketLineChart>
    </Frame>
  )
}

/** Prompt tokens per second of wall clock, per bucket. */
export function PromptTokensPerSecondChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const rates = buckets.map((bucket) => bucket.promptTokensPerSecond)
  const average = formatRate(summary.promptTokensPerSecond)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={average} label="prompt tokens / sec" />}
      legend={<Legend items={[{ label: 'Avg', mark: <LineMark stroke={CHART_1} />, value: average }]} />}
      alt={`Prompt tokens per second ranged from ${extent(rates, formatRate)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} allowDecimals />}
        unit="tokens / sec"
        lines={(bucket) => [
          { label: 'Avg', value: formatRate(bucket.promptTokensPerSecond) },
          { label: 'Prompt tokens', value: bucket.promptTokens.toLocaleString() },
        ]}
      >
        <SeriesLine dataKey="promptTokensPerSecond" stroke={CHART_1} />
      </BucketLineChart>
    </Frame>
  )
}

/** Average prompt and output tokens per request, per bucket. */
export function RequestSizeChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const prompts = measured(buckets, (bucket) => bucket.promptPerRequest)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatTokens(summary.promptPerRequest)} label="prompt tokens / request" />}
      legend={
        <Legend
          items={[
            { label: INPUT.label, mark: <LineMark stroke={INPUT.color} />, value: formatTokens(summary.promptPerRequest) },
            { label: OUTPUT.label, mark: <LineMark stroke={OUTPUT.color} />, value: formatTokens(summary.completionPerRequest) },
          ]}
        />
      }
      alt={`Prompt tokens per request ranged from ${extent(prompts, formatTokens)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatCountTick} />}
        unit="tokens / request"
        lines={(bucket) => [
          { label: INPUT.label, value: formatTokens(bucket.promptPerRequest) },
          { label: OUTPUT.label, value: formatTokens(bucket.completionPerRequest) },
        ]}
      >
        <SeriesLine dataKey="completionPerRequest" stroke={OUTPUT.color} />
        <SeriesLine dataKey="promptPerRequest" stroke={INPUT.color} />
      </BucketLineChart>
    </Frame>
  )
}

/** The share of each bucket's prompt tokens the provider read from its cache. */
export function PromptCacheChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const rates = measured(buckets, (bucket) => bucket.cacheRate)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatPercent(summary.cacheRate)} label="of prompt tokens cached" />}
      legend={
        <Legend
          items={[{ label: 'Cached', mark: <LineMark stroke={CHART_1} />, value: formatPercent(summary.cacheRate) }]}
        />
      }
      alt={`Prompt cache rate ranged from ${extent(rates, formatPercent)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatPercentTick} max={100} />}
        unit="percent"
        lines={(bucket) => [
          { label: 'Cached', value: formatPercent(bucket.cacheRate) },
          { label: 'Cached tokens', value: bucket.cachedPromptTokens.toLocaleString() },
          { label: 'Prompt tokens', value: bucket.promptTokens.toLocaleString() },
        ]}
      >
        <SeriesLine dataKey="cacheRate" stroke={CHART_1} />
      </BucketLineChart>
    </Frame>
  )
}

/** The share of each bucket's requests that failed, in percent. */
export function ErrorRateChart({ buckets, summary, hours, drag }: ChartProps): React.JSX.Element {
  const rates = measured(buckets, (bucket) => bucket.errorRate)
  return (
    <Frame
      pan={drag.pan}
      lead={<Headline value={formatPercent(summary.errorRate)} label="error rate" />}
      legend={
        <Legend
          items={[{ label: 'Errors', mark: <LineMark stroke={ERROR_STROKE} />, value: formatPercent(summary.errorRate) }]}
        />
      }
      alt={`Error rate ranged from ${extent(rates, formatPercent)} across this window's buckets.`}
    >
      <BucketLineChart
        data={buckets}
        hours={hours}
        drag={drag}
        axis={<ValueAxis format={formatPercentTick} max={errorAxisMax(buckets)} />}
        unit="percent"
        lines={(bucket) => [
          { label: 'Error rate', value: formatPercent(bucket.errorRate) },
          { label: 'Errors', value: bucket.errors.toLocaleString() },
          { label: 'Requests', value: bucket.requests.toLocaleString() },
        ]}
      >
        <SeriesLine dataKey="errorRate" stroke={ERROR_STROKE} />
      </BucketLineChart>
    </Frame>
  )
}
